<script lang="ts" setup>
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

let highlightCleanup: (() => void) | undefined;
let highlightTimeout: number | undefined;
let highlightCleanupTimeout: number | undefined;

const highlightEffectClasses = ['ring-2', 'ring-ph-green-500', 'ring-offset-2', 'ring-offset-white', 'bg-ph-green-50'];
const highlightTransitionClasses = ['motion-safe:transition-shadow', 'motion-safe:transition-colors', 'motion-safe:duration-300', 'motion-safe:ease-in-out'];

const waitForScrollTop = async () =>
  await new Promise<void>((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    if (window.scrollY <= 1) {
      resolve();
      return;
    }

    const maxWaitMs = 2000;
    const start = window.performance.now();
    let lastTop = window.scrollY;
    let stableFrames = 0;

    const tick = () => {
      const current = window.scrollY;
      const nearTop = current <= 1;
      const delta = Math.abs(current - lastTop);

      if (nearTop && delta < 1) {
        stableFrames += 1;
        if (stableFrames >= 2) {
          resolve();
          return;
        }
      } else {
        stableFrames = 0;
      }

      lastTop = current;

      if (window.performance.now() - start >= maxWaitMs) {
        resolve();
        return;
      }

      window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);
  });

const triggerHighlight = (inputElement: HTMLInputElement) => {
  highlightCleanup?.();
  if (highlightTimeout !== undefined) {
    window.clearTimeout(highlightTimeout);
    highlightTimeout = undefined;
  }
  if (highlightCleanupTimeout !== undefined) {
    window.clearTimeout(highlightCleanupTimeout);
    highlightCleanupTimeout = undefined;
  }

  const cleanup = () => {
    inputElement.classList.remove(...highlightEffectClasses, ...highlightTransitionClasses);
    if (highlightTimeout !== undefined) {
      window.clearTimeout(highlightTimeout);
      highlightTimeout = undefined;
    }
    if (highlightCleanupTimeout !== undefined) {
      window.clearTimeout(highlightCleanupTimeout);
      highlightCleanupTimeout = undefined;
    }
    if (highlightCleanup === cleanup) {
      highlightCleanup = undefined;
    }
  };

  highlightCleanup = cleanup;
  inputElement.classList.remove(...highlightEffectClasses, ...highlightTransitionClasses);
  window.requestAnimationFrame(() => {
    inputElement.classList.add(...highlightTransitionClasses, ...highlightEffectClasses);
  });
  highlightTimeout = window.setTimeout(() => {
    inputElement.classList.remove(...highlightEffectClasses);
    highlightCleanupTimeout = window.setTimeout(cleanup, 400);
  }, 400);
};

const onClick = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.scrollTo({ top: 0, behavior: 'smooth' });
  await waitForScrollTop();

  const inputElement = document.querySelector<HTMLInputElement>('[data-search-box-input="true"]');
  if (!inputElement) {
    return;
  }

  inputElement.focus({ preventScroll: true });
  triggerHighlight(inputElement);
};
</script>

<template>
  <div class="w-full">
    <button class="no-print hover:bg-ph-green-600 bg-ph-green-700 mx-auto block cursor-pointer rounded-lg px-4 py-2 text-xl text-white" @click.prevent="onClick">
      <FontAwesomeIcon :icon="faMagnifyingGlass" class="text-ph-green-200 mr-2" />
      <span>{{ t('components.more-results-button.text') }}</span>
    </button>
  </div>
</template>

<style lang="css" scoped></style>
